import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { KakaoAsset, KakaoAssetPurpose, KakaoPage, listKakaoAssets, uploadKakaoAsset } from '../../api/kakaoTemplate';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import Pagination from '../common/Pagination';
import { ConsoleInput, ConsoleLabel } from '../../styles/common/input.styled';
import { getErrorMessage } from '../../utils/apiMessage';

const Upload = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;
  p { margin: 0; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; }
`;
const FileInput = styled(ConsoleInput)`
  padding: 3px 6px;
  &::file-selector-button {
    height: 28px; margin-right: 8px; padding: 0 10px;
    border: 1px solid ${({ theme }) => theme.color.primary_400}; border-radius: 4px;
    background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100};
    font: inherit; cursor: pointer;
  }
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(145px, 1fr)); gap: 10px;`;
const AssetButton = styled.button`
  display: flex; flex-direction: column; gap: 8px; min-width: 0; padding: 8px;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  text-align: left; font: inherit; font-size: 12px; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.color.point_y_100}; background: ${({ theme }) => theme.color.primary_50}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  img, .fallback { width: 100%; height: 92px; border-radius: 4px; object-fit: contain; background: ${({ theme }) => theme.color.primary_50}; }
  .fallback { display: grid; place-items: center; color: ${({ theme }) => theme.color.text_color_300}; }
  strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; font-weight: 500; }
  small { color: ${({ theme }) => theme.color.text_color_300}; }
`;
const ErrorText = styled.p`margin: 0 0 12px; color: ${({ theme }) => theme.color.point_n_100}; font-size: 12px;`;
const compatibleTypes: Record<KakaoAssetPurpose, string[]> = {
  TEMPLATE_IMAGE: ['TEMPLATE_IMAGE', 'ATA'],
  HIGHLIGHT_IMAGE: ['HIGHLIGHT_IMAGE', 'KAKAO_HIGHLIGHT'],
  KAKAO_IMAGE: ['KAKAO_IMAGE', 'KAKAO'],
};

export default function KakaoAssetPicker({ purpose, onSelect, onClose }: {
  purpose: KakaoAssetPurpose; onSelect: (asset: KakaoAsset) => void; onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<KakaoPage<KakaoAsset> | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true); setLoadError(false);
    listKakaoAssets({ pg: page, limit: 20 })
      .then(data => { if (id === requestId.current) setResult(data); })
      .catch(() => { if (id === requestId.current) setLoadError(true); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
    return () => { requestId.current += 1; };
  }, [page, reloadKey]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('PNG 또는 JPG 이미지를 선택해 주세요.');
      return;
    }
    const maxKilobytes = purpose === 'HIGHLIGHT_IMAGE' ? 1000 : 500;
    if (file.size > maxKilobytes * 1024) {
      setError(`이미지는 ${maxKilobytes}KB 이하여야 합니다.`);
      return;
    }
    setUploading(true);
    try {
      onSelect(await uploadKakaoAsset(file, purpose));
    } catch (reason) {
      setError(getErrorMessage(reason, '이미지를 업로드하지 못했습니다.'));
    } finally {
      setUploading(false);
    }
  };

  return <Modal title="알림톡 이미지 선택" onClose={onClose} closeDisabled={uploading} historyMode="none" width={760}>
    <Upload>
      <ConsoleLabel htmlFor="kakao-image-upload">이미지 파일 업로드</ConsoleLabel>
      <FileInput id="kakao-image-upload" type="file" accept="image/png,image/jpeg" disabled={uploading} onChange={handleUpload} />
      <p>{purpose === 'HIGHLIGHT_IMAGE' ? 'PNG 또는 JPG · 1000KB 이하. 하이라이트 이미지는 정사각형으로 준비해 주세요.' : 'PNG 또는 JPG · 500KB 이하. 이미지형 권장 크기: 800×400px.'}</p>
    </Upload>
    {error && <ErrorText role="alert">{error}</ErrorText>}
    {loading ? <PageState status="loading" /> : loadError ? <PageState status="error" onRetry={() => setReloadKey(key => key + 1)} /> : !result?.content.length ? <PageState status="empty" title="등록된 이미지가 없습니다." /> : <>
      <Grid>{result.content.map(asset => <AssetButton type="button" key={asset.fileId} aria-label={asset.name || asset.originalName || asset.fileId} disabled={!compatibleTypes[purpose].includes(asset.type)} title={!compatibleTypes[purpose].includes(asset.type) ? '이 영역에 사용할 수 없는 이미지 유형입니다.' : undefined} onClick={() => onSelect(asset)}>
        {asset.url ? <img src={asset.url} alt="" /> : <span className="fallback">이미지</span>}
        <strong>{asset.name || asset.originalName || asset.fileId}</strong><small>{asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ''}{asset.type}</small>
      </AssetButton>)}</Grid>
      <Pagination currentPage={page} totalPages={result.totalPages} onPageChange={setPage} />
    </>}
  </Modal>;
}
